import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { API } from '@constants/app.constants';
import { HeaderLoader } from '@utils/headerLoader';
import { HttpResponseLoader } from '@utils/httpResponseLoader';
import { SessionRefreshService } from './session-refresh.service';

type HeaderMode = 'basic' | 'auth' | 'authRefresh' | 'authMultiPart';

interface RequestOptions {
  silent?: boolean;
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function makeOfflineError(): any {
  const err: any = new Error('offline');
  err.isOffline = true;
  err.status = 0;
  return err;
}

async function tryCacheLookup<T = any>(url: string): Promise<T | null> {
  try {
    if (typeof caches === 'undefined') return null;
    const cached = await caches.match(url);
    if (!cached) return null;
    const body: any = await cached.json();
    if (body?.success === true) return body.data as T;
    return body as T;
  } catch {
    return null;
  }
}

export interface BlobResponse {
  blob: Blob;
  filename: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiClientService {
  constructor(
    private http: HttpClient,
    private headerLoader: HeaderLoader,
    private httpResponseLoader: HttpResponseLoader,
    private sessionRefresh: SessionRefreshService,
  ) {}

  async get<T = any>(
    path: string,
    mode: HeaderMode = 'auth',
    options: RequestOptions = {},
  ): Promise<T> {
    if (isOffline()) {
      const cached = await tryCacheLookup<T>(`${API}${path}`);
      if (cached !== null) return cached;
      throw makeOfflineError();
    }
    return await this.request(mode, options, (headers) =>
      this.http.get(`${API}${path}`, headers),
    );
  }

  async post<T = any>(
    path: string,
    body: unknown = '',
    mode: HeaderMode = 'auth',
    options: RequestOptions = {},
  ): Promise<T> {
    if (isOffline()) {
      throw makeOfflineError();
    }
    return await this.request(mode, options, (headers) =>
      this.http.post(`${API}${path}`, body, headers),
    );
  }

  async patch<T = any>(
    path: string,
    body: unknown = '',
    mode: HeaderMode = 'auth',
    options: RequestOptions = {},
  ): Promise<T> {
    if (isOffline()) {
      throw makeOfflineError();
    }
    return await this.request(mode, options, (headers) =>
      this.http.patch(`${API}${path}`, body, headers),
    );
  }

  async delete<T = any>(
    path: string,
    mode: HeaderMode = 'auth',
    options: RequestOptions = {},
  ): Promise<T> {
    if (isOffline()) {
      throw makeOfflineError();
    }
    return await this.request(mode, options, (headers) =>
      this.http.delete(`${API}${path}`, headers),
    );
  }

  private async request<T = any>(
    mode: HeaderMode,
    options: RequestOptions,
    exec: (headers: any) => Observable<any>,
  ): Promise<T> {
    const refreshable = mode === 'auth' || mode === 'authMultiPart';
    const on401 = refreshable ? 'defer' : 'toastOnly';

    const headers = await this.resolveHeaders(mode);

    try {
      return await this.httpResponseLoader.filter(exec(headers), {
        ...options,
        on401,
      });
    } catch (error: any) {
      if (error?.status !== 401 || !refreshable) {
        throw error;
      }

      const refreshed = await this.sessionRefresh.refresh();

      if (refreshed) {
        const retryHeaders = await this.resolveHeaders(mode);
        try {
          return await this.httpResponseLoader.filter(exec(retryHeaders), {
            ...options,
            on401,
          });
        } catch (retryError: any) {
          if (retryError?.status === 401) {
            await this.httpResponseLoader.endSession(options);
          }
          throw retryError;
        }
      }

      await this.httpResponseLoader.endSession(options);
      throw error;
    }
  }

  async getBlob(
    path: string,
    fallbackFilename = 'download',
    mode: HeaderMode = 'auth',
  ): Promise<BlobResponse> {
    if (isOffline()) {
      throw makeOfflineError();
    }
    const baseHeaders: any = await this.resolveHeaders(mode);
    const headers: any = { ...(baseHeaders.headers ?? {}) };
    delete headers['Accept'];
    delete headers['Content-Type'];

    const response: any = await firstValueFrom(
      this.http.get(`${API}${path}`, {
        headers,
        responseType: 'blob',
        observe: 'response',
      }),
    );

    const blob = response?.body as Blob;
    const disposition = response?.headers?.get('Content-Disposition') ?? '';
    const filename = this.parseFilename(disposition) || fallbackFilename;

    return { blob, filename };
  }

  private parseFilename(contentDisposition: string): string {
    if (!contentDisposition) return '';
    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
    const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return match?.[1] ?? '';
  }

  private async resolveHeaders(mode: HeaderMode) {
    switch (mode) {
      case 'basic':
        return this.headerLoader.basic();

      case 'authRefresh':
        return await this.headerLoader.authRefresh();

      case 'authMultiPart':
        return await this.headerLoader.authMultiPart();

      case 'auth':
      default:
        return await this.headerLoader.auth();
    }
  }
}
